import React from 'react'
import { Box, Typography } from '@mui/material'
import { type SurveyCompletionRate, DivisionCategory } from '../../../../models/Division'
import { BookOutlined, AccessTime, AccountTreeOutlined } from '@mui/icons-material'
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import ShuffleIcon from '@mui/icons-material/Shuffle'
import { Link, useSearchParams } from 'react-router-dom'
import ROUTES from '../../../../consts/paths'
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward'
import { grey } from '../../../../colors'
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined'
import { getPercent } from '../../../../helpers'
import pluralize from 'pluralize'
import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined'
import CustomTooltip from '../../../../components/CustomTooltip'
export interface ReportsHeaderProps {
  completionRate: SurveyCompletionRate
  parentSurveyScore?: number | null
}

const ReportAttributes: React.FC<ReportsHeaderProps> = ({
  completionRate,
  parentSurveyScore

}): JSX.Element => {
  const [searchParams] = useSearchParams()
  const termId = searchParams.get('termId')
  const seriesId = searchParams.get('seriesId')
  const iconStyle = {
    sx: {
      color: 'iconColor'
    }
  }
  const percent = getPercent(completionRate.completionCount, completionRate.studentCount)
  const progressAriaLabel =
    completionRate.isAnonymousSurvey
      ? `${completionRate.studentCount} Responses`
      : ` ${completionRate.completionCount}/${completionRate.studentCount} (${percent}%) Responses`
  const parentScore = parentSurveyScore
  const formattedParentScore = parentScore?.toFixed(parentScore === 0 ? 1 : 2)
  const SurveyScoreTooltip = (): any => (
    <Box>
      <Typography
        variant="body2"
        sx={{ fontSize: 12, fontWeight: 600, color: 'white', mb: 0.5 }}
      >
        Survey Score
      </Typography>
      <Typography variant="body2" sx={{ fontSize: 12, color: 'white' }}>
        This score is calculated as the average of all linear scale questions in the survey. Higher
        scores may indicate more positive sentiment, but do not substitute reviewing the full report.
      </Typography>
    </Box>
  )
  function renderSubtitle (): JSX.Element {
    switch (completionRate.categoryName) {
      case DivisionCategory.Course:
        return <Box display="flex" alignItems="center" height="100%" flexWrap="wrap" gap={3}>
          <Box display="flex" alignItems="center" height="100%" flexWrap="nowrap" gap={0.75}>
            <BookOutlined {...iconStyle} />{completionRate.abbreviation}
          </Box>
          <Box display="flex" alignItems="center" height="100%" gap={0.75}>
            <AccessTime {...iconStyle} />{completionRate?.childrenCount} {pluralize('Section', completionRate.childrenCount)}
          </Box>
          <Box display="flex" alignItems="center" height="100%" gap={0.75}>
            <VerifiedOutlinedIcon {...iconStyle} />{progressAriaLabel.trim()}
          </Box>
          {formattedParentScore && formattedParentScore !== '0.0' && (
            <>
              <CustomTooltip title={SurveyScoreTooltip()} placement="bottom">
                <LeaderboardOutlinedIcon {...iconStyle} />
                {formattedParentScore}
              </CustomTooltip>
            </>
          )}
        </Box>
      case DivisionCategory.Section:
        return <Box display="flex" alignItems="center" height="100%" flexWrap="wrap" gap={3}>
          <Box display="flex" alignItems="center" height="100%" flexWrap="nowrap" gap={0.75}>
            <AccessTime {...iconStyle} />{completionRate.abbreviation}
          </Box>
          {completionRate?.crosslistedAbbrevs &&
            <Box display="flex" alignItems="center" height="100%" flexWrap="nowrap" gap={0.75}>
              <ShuffleIcon {...iconStyle} />{completionRate?.crosslistedAbbrevs}
            </Box>}
          <Box display="flex" alignItems="center" height="100%" flexWrap="nowrap" gap={0.75}>
            <VerifiedOutlinedIcon {...iconStyle} />{progressAriaLabel}
          </Box>

          <Box display="flex" alignItems="center" height="100%" flexWrap="wrap" gap={0.75}>
            <AccountCircleOutlinedIcon {...iconStyle} />
            {completionRate?.instructorDetails?.map((instructor, index) => {
              const isLastInstructor = index === completionRate?.instructorDetails.length - 1
              return (<Box flex="display" display="flex" alignItems="center" height="100%" key={index}>
                <Link
                  target='_blank'
                  style={{ color: grey[700], whiteSpace: 'nowrap' }}
                  to={`${ROUTES.INSTRUCTOR_SURVEYS}/${instructor?.id ?? ''}?termId=${termId}&seriesId=${seriesId}`}
                >
                  {instructor?.title}
                </Link>
                <ArrowOutwardIcon sx={{ fontSize: '20px' }} />
                {!isLastInstructor && completionRate?.instructorDetails.length > 1 && ', '}
              </Box>
              )
            })}
          </Box>
          <Box display="flex" alignItems="center" height="100%" flexWrap="nowrap" gap={0.75}>
            {formattedParentScore && formattedParentScore !== '0.0' && (
              <>
                <CustomTooltip title={SurveyScoreTooltip()} placement="bottom">
                  <LeaderboardOutlinedIcon {...iconStyle} />
                  {formattedParentScore}
                </CustomTooltip>
              </>
            )}
          </Box>
        </Box>
      default:
        return <Box display="flex" alignItems="center" height="100%" gap={3}>
          <Box display="flex" alignItems="center" height="100%" flexWrap="nowrap" gap={0.75}><AccountTreeOutlined {...iconStyle} />{completionRate.childrenCount} {pluralize(completionRate?.childrenCategory?.childCategoryName ?? '', completionRate.childrenCount)}</Box>
          <Box display="flex" alignItems="center" height="100%" flexWrap="nowrap" gap={0.75}><VerifiedOutlinedIcon {...iconStyle} />{progressAriaLabel.trim()}</Box>
          <Box display="flex" alignItems="center" height="100%" flexWrap="nowrap" gap={0.75}>
            {formattedParentScore && formattedParentScore !== '0.0' && (
              <>
                <CustomTooltip title={SurveyScoreTooltip()} placement="bottom">
                  <LeaderboardOutlinedIcon {...iconStyle} />
                  {formattedParentScore}
                </CustomTooltip>
              </>
            )}
          </Box>
        </Box>
    }
  }
  return (
    <Box>
      <Typography tabIndex={0} variant="h3">
        {renderSubtitle()}
      </Typography>
    </Box>
  )
}

export default ReportAttributes
